import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Settings, Trash2, Bell, BellOff, Search } from 'lucide-react';
import ConfirmModal from '../components/Common/ConfirmModal';
import useConfirmDialog from '../hooks/useConfirmDialog';
import {
  getLocalSavedSearches,
  addLocalSavedSearch,
  updateLocalSavedSearch,
  deleteLocalSavedSearch,
  LocalSavedSearch,
} from '../services/localSavedSearches';
import './SavedSearches.scss';

interface SavedSearch {
  id: number | string;
  title: string;
  
  // Employee search fields
  keywords?: string;
  work_field?: string[];
  job_type?: string[];
  work_time?: string[];
  salary_min?: number;
  company_activity?: string;
  
  // Employer search fields
  positions?: string[] | string;
  education_level?: string[] | string;
  languages?: string[] | string;
  technical_skills?: string[] | string;
  experience_years?: string;
  desired_work_type?: string[] | string;
  desired_salary?: string;
  
  // Common fields
  location?: string[];
  is_subscribed: boolean;
  notification_frequency?: 'immediate' | 'daily' | 'weekly';
  total_results: number;
  new_results: number;
  created_at: string;
  updated_at: string;
}

interface SearchFilters {
  title: string;
  keywords: string[];
  location: string[];
  work_field: string[];
  job_type: string[];
  work_time: string[];
  salary_min: string;
  company_activity: string;
  is_subscribed: boolean;
  notification_frequency: 'immediate' | 'daily' | 'weekly';
}

const SavedSearches: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmployerMode = searchParams.get('mode') === 'employer';
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
  const [searchCounts, setSearchCounts] = useState<Record<string | number, { total: number; newCount: number }>>({});
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    title: '',
    keywords: [],
    location: [],
    work_field: [],
    job_type: [],
    work_time: [],
    salary_min: '',
    company_activity: '',
    is_subscribed: true,
    notification_frequency: 'daily'
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<number | string | null>(null);
  const [selectedSearches, setSelectedSearches] = useState<(number | string)[]>([]);
  const [searchTitleFilter, setSearchTitleFilter] = useState('');

  const mapLocalToSavedSearch = (ls: LocalSavedSearch): SavedSearch => ({
    id: ls.id,
    title: ls.name,
    keywords: ls.keywords.join(', ') || undefined,
    location: ls.location,
    work_field: ls.work_field,
    job_type: ls.job_type,
    work_time: ls.work_time,
    salary_min: ls.salary_min ?? undefined,
    is_subscribed: false,
    total_results: 0,
    new_results: 0,
    created_at: ls.created_at,
    updated_at: ls.created_at,
  });

  const fetchSavedSearches = useCallback(async () => {
    try {
      setLoading(true);

      if (!isAuthenticated) {
        const local = getLocalSavedSearches().map(mapLocalToSavedSearch);
        setSavedSearches(local);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const endpoint = isEmployerMode 
        ? `${API_URL}/employer-saved-searches/my`
        : `${API_URL}/saved-searches/my`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSavedSearches(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching saved searches:', error);
    } finally {
      setLoading(false);
    }
  }, [isEmployerMode, isAuthenticated]);

  const buildSearchParams = (search: SavedSearch): URLSearchParams => {
    const params = new URLSearchParams();
    if (search.keywords) params.set('query', search.keywords);
    if (search.location && search.location.length > 0) params.set('location', search.location.join(','));
    if (search.work_field && search.work_field.length > 0) params.set('workField', search.work_field.join(','));
    if (search.job_type && search.job_type.length > 0) params.set('jobType', search.job_type.join(','));
    if (search.work_time && search.work_time.length > 0) params.set('workTime', search.work_time.join(','));
    if (search.salary_min) params.set('salaryMin', search.salary_min.toString());
    if (search.company_activity) params.set('companyActivity', search.company_activity);
    return params;
  };

  const fetchSearchCounts = useCallback(async (searches: SavedSearch[]) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const counts: Record<string | number, { total: number; newCount: number }> = {};

    await Promise.all(searches.map(async (search) => {
      try {
        const params = buildSearchParams(search);
        const paramsNew = new URLSearchParams(params);
        paramsNew.set('postedWithin', '3_days');

        const [totalRes, newRes] = await Promise.all([
          fetch(`${API_URL}/jobs/count?${params.toString()}`),
          fetch(`${API_URL}/jobs/count?${paramsNew.toString()}`)
        ]);

        const totalData = totalRes.ok ? await totalRes.json() : null;
        const newData = newRes.ok ? await newRes.json() : null;

        counts[search.id] = {
          total: totalData?.count ?? 0,
          newCount: newData?.count ?? 0
        };
      } catch (error) {
        console.error(`Error fetching count for search ${search.id}:`, error);
        counts[search.id] = { total: 0, newCount: 0 };
      }
    }));

    setSearchCounts(counts);
  }, []);

  useEffect(() => {
    fetchSavedSearches();
  }, [isAuthenticated, isEmployerMode, fetchSavedSearches]);

  useEffect(() => {
    if (savedSearches.length > 0) {
      fetchSearchCounts(savedSearches);
    }
  }, [savedSearches, fetchSearchCounts]);

  const handleCreateSearch = () => {
    setEditingSearch(null);
    setSearchFilters({
      title: '',
      keywords: [],
      location: [],
      work_field: [],
      job_type: [],
      work_time: [],
      salary_min: '',
      company_activity: '',
      is_subscribed: true,
      notification_frequency: 'daily'
    });
    setKeywordInput('');
    setShowCreateModal(true);
  };

  const handleEditSearch = (search: SavedSearch) => {
    setEditingSearch(search);
    // Parse keywords string into array
    const keywordsArray = search.keywords 
      ? search.keywords.split(',').map(k => k.trim()).filter(k => k)
      : [];
    
    setSearchFilters({
      title: search.title,
      keywords: keywordsArray,
      location: search.location || [],
      work_field: search.work_field || [],
      job_type: search.job_type || [],
      work_time: search.work_time || [],
      salary_min: search.salary_min?.toString() || '',
      company_activity: search.company_activity || '',
      is_subscribed: search.is_subscribed,
      notification_frequency: search.notification_frequency || 'daily'
    });
    setKeywordInput('');
    setShowCreateModal(true);
  };

  // Handle keyword chips
  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (keywordInput.trim()) {
        const newKeywords = keywordInput.trim().split(/\s+/);
        const uniqueKeywords = Array.from(new Set([...searchFilters.keywords, ...newKeywords]));
        setSearchFilters({ ...searchFilters, keywords: uniqueKeywords });
        setKeywordInput('');
      }
    }
  };

  const removeKeyword = (keyword: string) => {
    setSearchFilters({
      ...searchFilters,
      keywords: searchFilters.keywords.filter(k => k !== keyword)
    });
  };

  const handleSaveSearch = async () => {
    if (!isAuthenticated) {
      // Guest: save to localStorage
      if (editingSearch) {
        updateLocalSavedSearch(String(editingSearch.id), {
          name: searchFilters.title,
          keywords: searchFilters.keywords,
          location: searchFilters.location,
          work_field: searchFilters.work_field,
          job_type: searchFilters.job_type,
          work_time: searchFilters.work_time,
          salary_min: searchFilters.salary_min ? parseFloat(searchFilters.salary_min) : null,
        });
      } else {
        addLocalSavedSearch({
          name: searchFilters.title,
          keywords: searchFilters.keywords,
          location: searchFilters.location,
          work_field: searchFilters.work_field,
          job_type: searchFilters.job_type,
          work_time: searchFilters.work_time,
          salary_min: searchFilters.salary_min ? parseFloat(searchFilters.salary_min) : null,
        });
      }
      showToast(editingSearch ? 'Paieška atnaujinta!' : 'Paieška išsaugota!', 'success');
      setShowCreateModal(false);
      fetchSavedSearches();
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const payload = {
        title: searchFilters.title,
        keywords: searchFilters.keywords.length > 0 ? searchFilters.keywords.join(', ') : null,
        location: searchFilters.location.length > 0 ? searchFilters.location : null,
        work_field: searchFilters.work_field.length > 0 ? searchFilters.work_field : null,
        job_type: searchFilters.job_type.length > 0 ? searchFilters.job_type : null,
        work_time: searchFilters.work_time.length > 0 ? searchFilters.work_time : null,
        salary_min: searchFilters.salary_min ? parseFloat(searchFilters.salary_min) : null,
        company_activity: searchFilters.company_activity || null,
        is_subscribed: searchFilters.is_subscribed,
        notification_frequency: searchFilters.notification_frequency
      };

      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const url = editingSearch 
        ? `${API_URL}/${baseEndpoint}/${editingSearch.id}`
        : `${API_URL}/${baseEndpoint}`;

      const method = editingSearch ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        showToast(editingSearch ? 'Paieška atnaujinta!' : 'Paieška išsaugota!', 'success');
        setShowCreateModal(false);
        fetchSavedSearches();
      } else {
        showToast('Nepavyko išsaugoti paieškos', 'error');
      }
    } catch (error) {
      console.error('Error saving search:', error);
      showToast('Įvyko klaida išsaugant paiešką', 'error');
    }
  };

  const handleDeleteSearch = (id: number | string) => {
    setSearchToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!searchToDelete) return;

    if (!isAuthenticated) {
      deleteLocalSavedSearch(String(searchToDelete));
      fetchSavedSearches();
      setShowDeleteConfirm(false);
      setSearchToDelete(null);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const response = await fetch(`${API_URL}/${baseEndpoint}/${searchToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSavedSearches();
      } else {
        showToast('Nepavyko ištrinti paieškos', 'error');
      }
    } catch (error) {
      console.error('Error deleting search:', error);
      showToast('Įvyko klaida trinant paiešką', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setSearchToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setSearchToDelete(null);
  };

  const handleToggleSelectAll = () => {
    const filteredSearches = filterSearchesByTitle(savedSearches);
    
    if (selectedSearches.length === filteredSearches.length && filteredSearches.length > 0) {
      setSelectedSearches([]);
    } else {
      setSelectedSearches(filteredSearches.map(s => s.id));
    }
  };

  const handleToggleSelect = (id: number | string) => {
    setSelectedSearches(prev => 
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedSearches.length === 0) return;

    const confirmMessage = `Ar tikrai norite ištrinti ${selectedSearches.length} paieškų? Šio veiksmo negalima atšaukti.`;
    if (!await confirm(confirmMessage)) return;

    if (!isAuthenticated) {
      selectedSearches.forEach(id => deleteLocalSavedSearch(String(id)));
      setSelectedSearches([]);
      fetchSavedSearches();
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';

      await Promise.all(
        selectedSearches.map(id =>
          fetch(`${API_URL}/${baseEndpoint}/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })
        )
      );

      setSelectedSearches([]);
      fetchSavedSearches();
    } catch (error) {
      console.error('Error deleting searches:', error);
      showToast('Įvyko klaida trinant paieškas', 'error');
    }
  };

  const filterSearchesByTitle = (searches: SavedSearch[]) => {
    if (!searchTitleFilter.trim()) return searches;
    
    const filterLower = searchTitleFilter.toLowerCase().trim();
    return searches.filter(search => 
      search.title.toLowerCase().includes(filterLower)
    );
  };

  const handleToggleSubscription = async (id: number | string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const baseEndpoint = isEmployerMode ? 'employer-saved-searches' : 'saved-searches';
      const response = await fetch(`${API_URL}/${baseEndpoint}/${id}/toggle-subscription`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const current = savedSearches.find(s => s.id === id);
        const wasSubscribed = current?.is_subscribed;
        showToast(
          wasSubscribed ? 'Prenumerata išjungta' : 'Prenumerata įjungta! Paieška rodoma Prenumeratose.',
          wasSubscribed ? 'info' : 'success'
        );
        fetchSavedSearches();
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  };

  const handleGoToSearch = (search: SavedSearch) => {
    console.log('🔍 handleGoToSearch - Full search object:', search);
    console.log('🔍 handleGoToSearch - positions field:', search.positions);
    
    // Build search URL with filters
    const params = new URLSearchParams();
    
    if (isEmployerMode) {
      // Employer candidate search
      // Note: No need to set mode param anymore, route handles it
      
      // Positions (keywords) - parse if it's a JSON string
      if (search.positions) {
        console.log('🔍 handleGoToSearch - positions exists:', search.positions);
        try {
          const positionsArray = Array.isArray(search.positions) 
            ? search.positions 
            : JSON.parse(search.positions as string);
          console.log('🔍 handleGoToSearch - positionsArray:', positionsArray);
          if (positionsArray.length > 0) {
            params.set('keywords', positionsArray.join(','));
            console.log('✅ handleGoToSearch - Set keywords param:', positionsArray.join(','));
          }
        } catch (e) {
          console.error('❌ Error parsing positions:', e);
        }
      } else {
        console.log('❌ handleGoToSearch - No positions field!');
      }
      
      if (search.location && search.location.length > 0) {
        params.set('location', search.location.join(','));
      }
      
      console.log('🔍 handleGoToSearch - Final URL:', `/employer/candidates?${params.toString()}`);
      navigate(`/employer/candidates?${params.toString()}`);
    } else {
      // Employee job search
      if (search.keywords) params.set('keywords', search.keywords);
      if (search.location && search.location.length > 0) params.set('location', search.location.join(','));
      if (search.work_field && search.work_field.length > 0) params.set('workField', search.work_field.join(','));
      if (search.job_type && search.job_type.length > 0) params.set('jobType', search.job_type.join(','));
      if (search.work_time && search.work_time.length > 0) params.set('workTime', search.work_time.join(','));
      if (search.salary_min) params.set('salaryMin', search.salary_min.toString());
      if (search.company_activity) params.set('companyActivity', search.company_activity);

      navigate(`/darbo-skelbimai?${params.toString()}`);
    }
  };

  const toggleMultiSelect = (field: keyof SearchFilters, value: string) => {
    const current = searchFilters[field] as string[];
    if (current.includes(value)) {
      setSearchFilters({
        ...searchFilters,
        [field]: current.filter(v => v !== value)
      });
    } else {
      setSearchFilters({
        ...searchFilters,
        [field]: [...current, value]
      });
    }
  };

  const formatCriteriaFlat = (search: SavedSearch): string => {
    const parts: string[] = [];

    if (search.keywords) parts.push(search.keywords);

    if (search.positions) {
      try {
        const arr = Array.isArray(search.positions) ? search.positions : JSON.parse(search.positions as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.location && search.location.length > 0) parts.push(...search.location);

    if (search.education_level) {
      try {
        const arr = Array.isArray(search.education_level) ? search.education_level : JSON.parse(search.education_level as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.languages) {
      try {
        const arr = Array.isArray(search.languages) ? search.languages : JSON.parse(search.languages as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.technical_skills) {
      try {
        const arr = Array.isArray(search.technical_skills) ? search.technical_skills : JSON.parse(search.technical_skills as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.experience_years) parts.push(search.experience_years);

    if (search.desired_work_type) {
      try {
        const arr = Array.isArray(search.desired_work_type) ? search.desired_work_type : JSON.parse(search.desired_work_type as string);
        if (arr.length > 0) parts.push(...arr);
      } catch { /* ignore */ }
    }

    if (search.desired_salary) parts.push(search.desired_salary);
    if (search.work_field && search.work_field.length > 0) parts.push(...search.work_field);
    if (search.job_type && search.job_type.length > 0) parts.push(...search.job_type);
    if (search.salary_min) parts.push(`nuo ${search.salary_min}€`);

    return parts.length > 0 ? parts.join(', ') : 'Visi skelbimai';
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="saved-searches-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if wrong user type for the mode (only for authenticated users)
  if (isAuthenticated && isEmployerMode && user?.user_type !== 'employer') {
    return null;
  }
  if (isAuthenticated && !isEmployerMode && user?.user_type !== 'employee' && user?.user_type !== 'job_seeker') {
    return null;
  }

  return (
    <div className="saved-searches-page">
      {ConfirmDialogNode}
      <div className="container">
        {/* Header row: breadcrumbs left, filters right */}
        <div className="header-row">
          <div className="breadcrumbs">
            <span onClick={() => navigate('/')}>Pradžia</span>
            <span className="separator">&gt;</span>
            <span className="current">Išsaugotos paieškos</span>
          </div>
          <div className="filters-toolbar">
            <div className="select-all-dropdown">
              <button 
                className="select-all-btn"
                onClick={handleToggleSelectAll}
              >
                <input 
                  type="checkbox" 
                  checked={selectedSearches.length > 0 && selectedSearches.length === filterSearchesByTitle(savedSearches).length}
                  onChange={handleToggleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>Žymėti visus</span>
              </button>
            </div>
            <button 
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedSearches.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input 
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={searchTitleFilter}
                onChange={(e) => setSearchTitleFilter(e.target.value)}
              />
              <Search size={18} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={handleCreateSearch}>
              <Plus size={18} />
              <span>Nauja paieška</span>
            </button>
          </div>
        </div>

        {/* Searches Content */}
        <div className="searches-section">
          {loading ? (
            <div className="loading">Kraunama...</div>
          ) : filterSearchesByTitle(savedSearches).length === 0 ? (
            <div className="empty-state">
              <Search size={48} />
              <p>{searchTitleFilter ? 'Paieškų pagal šį filtrą nerasta' : 'Dar neturite išsaugotų paieškų'}</p>
              {!searchTitleFilter && (
                <button className="btn-primary" onClick={handleCreateSearch}>
                  Sukurti pirmą paiešką
                </button>
              )}
            </div>
          ) : (
            <div className="searches-grid">
              {filterSearchesByTitle(savedSearches).map((search) => (
                <div key={search.id} className={`search-card ${selectedSearches.includes(search.id) ? 'selected' : ''}`}>
                  <div className="search-card-header">
                    <div className="header-left">
                      <input
                        type="checkbox"
                        checked={selectedSearches.includes(search.id)}
                        onChange={() => handleToggleSelect(search.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {isAuthenticated && (
                        <button
                          className={`subscription-label ${search.is_subscribed ? 'active' : ''}`}
                          onClick={() => handleToggleSubscription(search.id)}
                        >
                          {search.is_subscribed ? <Bell size={14} /> : <BellOff size={14} />}
                          <span>{search.is_subscribed ? 'Prenumeruojama' : 'Prenumeruoti'}</span>
                        </button>
                      )}
                    </div>
                    <div className="header-actions">
                      <button
                        className="btn-icon"
                        onClick={() => handleEditSearch(search)}
                        title="Nustatymai"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDeleteSearch(search.id)}
                        title="Ištrinti"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="search-card-body" onClick={() => handleGoToSearch(search)}>
                    <h3 className="search-title">{search.title}</h3>
                    <div className="search-criteria">
                      <span className="criteria-label">Kriterijai:</span>
                      <p className="criteria-text">{formatCriteriaFlat(search)}</p>
                    </div>
                  </div>

                  <div className="search-card-footer">
                    <span className="result-count">
                      Iš viso: <strong>{searchCounts[search.id]?.total ?? '...'}</strong>
                    </span>
                    <span className="divider" />
                    <span className="new-count">
                      Naujų: <strong>{searchCounts[search.id]?.newCount ?? '...'}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h2>{editingSearch ? 'Redaguoti paiešką' : 'Nauja paieška'}</h2>
              <button className="btn-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="searchTitle">Paieškos pavadinimas *</label>
                <input
                  type="text"
                  id="searchTitle"
                  value={searchFilters.title}
                  onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
                  placeholder="pvz., Pardavimų / Marketingo vadovai"
                />
              </div>

              <div className="form-group">
                <label htmlFor="keywords">Raktažodžiai</label>
                <div className="keyword-input-container">
                  {/* Display keyword chips */}
                  {searchFilters.keywords.map((keyword) => (
                    <button
                      key={keyword}
                      type="button"
                      className="keyword-chip-inline"
                      onClick={() => removeKeyword(keyword)}
                    >
                      <span>{keyword}</span>
                      <span className="remove-chip">×</span>
                    </button>
                  ))}
                  
                  {/* Input field */}
                  <input
                    type="text"
                    id="keywords"
                    className="keyword-input-inline"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    placeholder={searchFilters.keywords.length === 0 ? "pvz., programuotojas, vadovas" : ""}
                  />
                </div>
                <small className="form-hint">Spausk tarpą arba Enter kad pridėti raktažodį</small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Miestas</label>
                  <div className="checkbox-grid">
                    {['Vilnius', 'Kaunas', 'Klaipėda', 'Šiauliai', 'Panevėžys'].map(city => (
                      <label key={city} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={searchFilters.location.includes(city)}
                          onChange={() => toggleMultiSelect('location', city)}
                        />
                        <span>{city}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Darbo sritis</label>
                  <div className="checkbox-grid">
                    {['Vadybininkai', 'IT', 'Pardavimai', 'Marketingas', 'Aptarnavimas'].map(field => (
                      <label key={field} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={searchFilters.work_field.includes(field)}
                          onChange={() => toggleMultiSelect('work_field', field)}
                        />
                        <span>{field}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Darbo tipas</label>
                  <div className="checkbox-grid">
                    {['Nuolatinis', 'Laikinas', 'Projektinis', 'Praktika'].map(type => (
                      <label key={type} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={searchFilters.job_type.includes(type)}
                          onChange={() => toggleMultiSelect('job_type', type)}
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Darbo laikas</label>
                  <div className="checkbox-grid">
                    {['Visą darbo dieną', 'Nepilną darbo dieną', 'Nuotoliniu būdu', 'Hibridinis'].map(time => (
                      <label key={time} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={searchFilters.work_time.includes(time)}
                          onChange={() => toggleMultiSelect('work_time', time)}
                        />
                        <span>{time}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="salaryMin">Minimali alga (€)</label>
                <input
                  type="number"
                  id="salaryMin"
                  value={searchFilters.salary_min}
                  onChange={(e) => setSearchFilters({ ...searchFilters, salary_min: e.target.value })}
                  placeholder="pvz., 1500"
                />
              </div>

              {isAuthenticated && (
                <>
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={searchFilters.is_subscribed}
                        onChange={(e) => setSearchFilters({ ...searchFilters, is_subscribed: e.target.checked })}
                      />
                      Prenumeruoti šią paiešką (gauti pranešimus apie naujus skelbimus)
                    </label>
                  </div>

                  {searchFilters.is_subscribed && (
                    <div className="form-group">
                      <label>Skelbimo siuntimo dažnis</label>
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="notification_frequency"
                            value="immediate"
                            checked={searchFilters.notification_frequency === 'immediate'}
                            onChange={() => setSearchFilters({ ...searchFilters, notification_frequency: 'immediate' })}
                          />
                          <span>Nedelsiant, atsiradus naujam skelbimui</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="notification_frequency"
                            value="daily"
                            checked={searchFilters.notification_frequency === 'daily'}
                            onChange={() => setSearchFilters({ ...searchFilters, notification_frequency: 'daily' })}
                          />
                          <span>Kas dieną</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="notification_frequency"
                            value="weekly"
                            checked={searchFilters.notification_frequency === 'weekly'}
                            onChange={() => setSearchFilters({ ...searchFilters, notification_frequency: 'weekly' })}
                          />
                          <span>Kas savaitę</span>
                        </label>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                Atšaukti
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveSearch}
                disabled={!searchFilters.title.trim()}
              >
                {editingSearch ? 'Atnaujinti' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        message="Ar tikrai norite visiškai ištrinti šią paiešką? Šio veiksmo negalima atšaukti."
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default SavedSearches;

